import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGhgOrganization } from 'src/common/hooks';
import { OrganizationTag } from 'src/utils/tags';
import {
  useCommitmentTarget,
  useDeclineCommitmentTarget,
  useRabenConsent,
} from 'src/Ghg/hooks';
import { DeclinePayload } from 'src/Ghg/interfaces';
import DeclineReasonForm from '../DeclineReasonForm';

// C16's sidebar route for the "Supplier Commitment" tab (specs/001-raben-
// country-masteradmin/mass-specs/specs/20260826-decl-commitment-tab-pdf.md
// Goal 1) -- that tab doesn't exist yet (out of scope here), but the route
// path is already pinned there so this button target won't need to change
// when C16 lands.
export const SUPPLIER_COMMITMENT_TAB_PATH = '/ghg/supplier-commitment';

// C15 (pop-up 2 -- target prompt): same spec as RabenConsentModal. Shown
// for carriers who already ACCEPTED (consent === true) and have at least one
// base year the server calls eligible. BR-4/EC-5: gating on `consent === true`
// here is mutually exclusive with pop-up 1's `consent === null` gate, so the
// two pop-ups can never both be eligible for the same org -- no shared
// precedence state needed between these sibling components.
// "target not yet set" (Goal 2): once C17's CommitmentTarget exists for
// the org, the prompt's job is done -- it must never reappear after the
// target is set (verified live: without this check the modal kept showing
// on the dashboard right after the statement was generated). The GET is
// gated on the same consent === true condition as the modal itself, since
// the endpoint 409s before an accept.
//
// RG 28.08 8.4: "Odrzucam" (D3: optional reason, first step of two) records a
// refusal on the server (`declined`), after which the pop-up never comes back
// (D4: a change of mind happens on the tab). The gate follows the server's
// `eligible_years` instead of the scope 1/2 completion of whichever year the
// questionnaire currently shows. D2: only the carrier's own users decide
// (`is_direct_member`, strict -- a 403 on the POST redirects to /403), and
// there is no X / Esc, as with pop-up 1.
export default function TargetPromptModal() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'ghg.targetPromptModal',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const organization = useGhgOrganization();
  const [step, setStep] = React.useState<'prompt' | 'decline'>('prompt');
  const { data: consentData, isLoading: consentLoading } = useRabenConsent(
    organization.id
  );

  const isRaben = organization.tags.includes(OrganizationTag.Raben);

  // No raben_commitment_pilot gate (RG live demo 2026-10-02): an accepted
  // Letter is reached only through the window-gated pop-up or the tab.
  const consentAccepted =
    isRaben &&
    !consentLoading &&
    consentData?.consent === true &&
    consentData?.is_direct_member === true;

  const { data: targetState } = useCommitmentTarget(organization.id, {
    enabled: consentAccepted,
  });
  const declineTarget = useDeclineCommitmentTarget(organization.id);

  // Once the carrier is actually on the tab this points to, don't cover it
  // with the very prompt that sent them there.
  const onSupplierCommitmentTab =
    location.pathname === SUPPLIER_COMMITMENT_TAB_PATH;

  const show =
    consentAccepted &&
    // strict null: `undefined` (still loading / errored) keeps it hidden,
    // an existing target keeps it hidden forever
    targetState?.target === null &&
    // D4: a refusal is final for the pop-up
    !targetState.declined &&
    Array.isArray(targetState.eligible_years) &&
    targetState.eligible_years.length > 0 &&
    !onSupplierCommitmentTab;

  // Back to the prompt itself whenever the dialog goes away (refusal
  // recorded, target set, navigation), so it never reopens on the form.
  React.useEffect(() => {
    if (!show) setStep('prompt');
  }, [show]);

  const handleGoToTab = () => {
    navigate(SUPPLIER_COMMITMENT_TAB_PATH);
  };

  // The dialog disappears by itself once the refetched state has `declined`.
  const handleDecline = async (payload: DeclinePayload) => {
    await declineTarget.mutateAsync(payload);
  };

  return (
    <Dialog open={!!show} onClose={() => {}} disableEscapeKeyDown>
      <DialogTitle>{t('title')}</DialogTitle>
      {step === 'prompt' ? (
        <>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('description')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={() => setStep('decline')}>
              {t('reject')}
            </Button>
            <Button variant="contained" onClick={handleGoToTab}>
              {t('goToTab')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <DialogContent>
          <DeclineReasonForm
            context="target"
            options={targetState?.decline_reason_options}
            loading={declineTarget.isLoading}
            onConfirm={handleDecline}
            onCancel={() => setStep('prompt')}
          />
        </DialogContent>
      )}
    </Dialog>
  );
}
